// Plúr — perforated hero.
// The header film is seen only through the dots of the PLÚR mark, the same
// perforation printed on the bags. The pointer acts as a lens that opens the
// dots around it; scrolling opens every dot until the whole field fills the
// frame. If WebGL is unavailable the plain <video> is shown full-bleed.
import * as THREE from 'three';

const canUse3d = () => {
  if (window.theme && window.theme.enable3d === false) return false;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch (e) {
    return false;
  }
};

function init(canvas) {
  const hero = canvas.closest('[data-hero]');
  const media = canvas.closest('[data-hero-media]');
  const video = media.querySelector('video');
  if (!video) return;

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true, premultipliedAlpha: true });
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setClearColor(0x000000, 0);
  const dpr = Math.min(window.devicePixelRatio, 2);
  renderer.setPixelRatio(dpr);

  const tex = new THREE.VideoTexture(video);
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;

  const uniforms = {
    uTex: { value: tex },
    uMark: { value: null },
    uRes: { value: new THREE.Vector2(1, 1) },
    uVid: { value: new THREE.Vector2(16, 9) },
    uMouse: { value: new THREE.Vector2(-9999, -9999) },
    uPitch: { value: (parseFloat(canvas.dataset.pitch) || 14) * dpr },
    uTime: { value: 0 },
    uIntro: { value: 0 },
    uReveal: { value: 0 },
    uHover: { value: 0 },
    uSmall: { value: matchMedia('(max-width: 749px)').matches ? 1 : 0 },
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthTest: false,
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform sampler2D uTex, uMark;
      uniform vec2 uRes, uVid, uMouse;
      uniform float uPitch, uTime, uIntro, uReveal, uHover, uSmall;
      varying vec2 vUv;

      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

      vec2 coverUv(vec2 uv, float zoom) {
        float rs = uRes.x / uRes.y, ri = uVid.x / uVid.y;
        vec2 s = rs < ri ? vec2(rs / ri, 1.0) : vec2(1.0, ri / rs);
        return (uv - 0.5) * s / zoom + 0.5;
      }

      void main() {
        vec2 px = vUv * uRes;
        float P = uPitch;
        vec2 cell = floor(px / P);
        vec2 c = (cell + 0.5) * P;
        float h = hash(cell);

        // Where the mark sits: centred, as tall as the frame allows.
        float size = min(uRes.y * mix(0.74, 0.7, uSmall), uRes.x * mix(0.9, 0.98, uSmall));
        vec2 m0 = vec2((uRes.x - size) * 0.5, (uRes.y - size) * 0.5 + uRes.y * 0.03);
        vec2 muv = (c - m0) / size;
        float inMark = 0.0;
        if (muv.x > 0.0 && muv.x < 1.0 && muv.y > 0.0 && muv.y < 1.0) inMark = step(0.25, texture2D(uMark, muv).a);

        // Dots pop in one by one, in a field-like scatter.
        float pop = smoothstep(h * 0.7, h * 0.7 + 0.3, uIntro);
        float r = mix(0.06, 0.44, inMark) * pop;

        // Pointer lens: opens the perforation around the cursor.
        float d = length(c - uMouse) / P;
        r = max(r, 0.5 * smoothstep(12.0, 0.0, d) * uHover);

        // Scroll reveal: every dot grows until they merge into the full frame.
        float rev = smoothstep(0.0, 1.0, clamp(uReveal * 1.25 - h * 0.25, 0.0, 1.0));
        r = mix(r, 0.76, rev);

        float dist = length(px - c) / P;
        float aa = 1.2 / P;
        float a = 1.0 - smoothstep(r - aa, r + aa, dist);

        // Slow push-out as the frame opens, and a breath of movement.
        float zoom = mix(1.18, 1.0, uReveal);
        vec2 drift = vec2(sin(uTime * 0.2), cos(uTime * 0.17)) * 0.003;
        vec3 col = texture2D(uTex, coverUv(vUv + drift, zoom)).rgb;
        col += (hash(vUv * uRes + fract(uTime) * 91.0) - 0.5) * 0.035;

        gl_FragColor = vec4(col * a, a);
      }`,
  });

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));

  let markReady = false;
  new THREE.TextureLoader().load(canvas.dataset.mark, (t) => {
    t.colorSpace = THREE.NoColorSpace;
    t.minFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    uniforms.uMark.value = t;
    markReady = true;
  });

  // --- pointer + scroll ----------------------------------------------------
  let hoverTarget = 0;
  let lastMove = 0;
  const onMove = (x, y) => {
    const r = canvas.getBoundingClientRect();
    uniforms.uMouse.value.set((x - r.left) * dpr, (r.height - (y - r.top)) * dpr);
    hoverTarget = 1;
    lastMove = performance.now();
  };
  hero.addEventListener('pointermove', (e) => onMove(e.clientX, e.clientY), { passive: true });
  hero.addEventListener('pointerleave', () => (hoverTarget = 0));
  hero.addEventListener('touchmove', (e) => e.touches[0] && onMove(e.touches[0].clientX, e.touches[0].clientY), { passive: true });

  let progress = 0;
  const readScroll = () => {
    const r = hero.getBoundingClientRect();
    const travel = Math.max(1, r.height - window.innerHeight);
    progress = Math.min(1, Math.max(0, -r.top / travel));
  };
  window.addEventListener('scroll', readScroll, { passive: true });

  const resize = () => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    uniforms.uRes.value.set(w * dpr, h * dpr);
    uniforms.uSmall.value = w < 750 ? 1 : 0;
    readScroll();
  };
  new ResizeObserver(resize).observe(canvas);

  let visible = true;
  new IntersectionObserver(([e]) => (visible = e.isIntersecting)).observe(hero);

  let started = false;
  let failed = false;
  const start = () => {
    if (started || failed || !video.videoWidth) return;
    uniforms.uVid.value.set(video.videoWidth, video.videoHeight);
    started = true;
  };
  video.addEventListener('loadeddata', start);
  video.addEventListener('playing', start);
  if (video.readyState >= 2) start();

  // Switch the layout into the tall, pinned version only once WebGL is live.
  hero.classList.add('is-gl');
  resize();

  const clock = new THREE.Clock();
  let smooth = 0;
  renderer.setAnimationLoop(() => {
    const dt = Math.min(clock.getDelta(), 0.05);
    if (!visible || document.hidden || failed) return;
    uniforms.uTime.value += dt;
    if (markReady) uniforms.uIntro.value = Math.min(1, uniforms.uIntro.value + dt / 1.8);
    if (performance.now() - lastMove > 900) hoverTarget = 0;
    uniforms.uHover.value += (hoverTarget - uniforms.uHover.value) * Math.min(1, dt * (hoverTarget ? 5 : 1.4));
    smooth += (progress - smooth) * Math.min(1, dt * 7);
    uniforms.uReveal.value = Math.min(1, smooth / 0.62);
    hero.style.setProperty('--p', smooth.toFixed(4));
    hero.classList.toggle('is-revealed', smooth > 0.45);
    if (!started || !markReady) return;
    try {
      renderer.render(scene, camera);
      media.classList.add('is-gl');
    } catch (e) {
      failed = true;
      hero.classList.remove('is-gl');
      hero.classList.add('no-gl');
      media.classList.remove('is-gl');
      renderer.setAnimationLoop(null);
      console.warn('[plur] hero WebGL disabled:', e);
    }
  });
}

const canvas = document.querySelector('[data-hero-gl]');
if (canvas && !canUse3d()) canvas.closest('[data-hero]').classList.add('no-gl');
if (canvas && canUse3d()) {
  try {
    init(canvas);
  } catch (e) {
    console.warn('[plur] hero WebGL disabled:', e);
  }
}
